import type { Policy } from '@mls-wallet/shared-types';
import { cn } from '@/lib/utils';
import {
  formatPolicyDetailsText,
  getPolicyApprovalDisplay,
} from '@/utils/policyFormatting';
import {
  TokenTransferLimit,
  type TokenTransferLimitProps,
} from '../shared/token-transfer-limit';

interface TransactionBuilderPolicyIndicatorProps {
  policy: Policy;
  className?: string;
  limitationUsage?: {
    usedAmount: string;
    remainingAmount: string;
  } | null;
  currentTransactionAmount?: string | null;
}

type TokenTransferLimitAdapterInput = {
  policy: Policy;
  limitationUsage:
    | {
        usedAmount: string;
        remainingAmount: string;
      }
    | null
    | undefined;
  currentTransactionAmount: string | null | undefined;
};

/**
 * Maps policy and current-transaction props to `TokenTransferLimitProps` for
 * the usage bar. Returns null when the policy is not a rate-limited
 * token-transfer type or when any required usage data is absent.
 *
 * Guard order: `limitationUsage` is checked first so the subsequent field
 * accesses can use non-optional member access.
 */
function getTokenTransferLimitProps({
  policy,
  limitationUsage,
  currentTransactionAmount,
}: TokenTransferLimitAdapterInput): TokenTransferLimitProps | null {
  // Case: Missing required usage data, return null
  if (
    !limitationUsage ||
    policy.transactionType !== 'TOKEN_TRANSFER' ||
    !policy.limitation ||
    !policy.tokenTransferCondition ||
    !policy.limitationUsage?.windowStart ||
    !policy.limitationUsage?.windowEnd ||
    typeof policy.limitationUsage.amountThreshold !== 'string' ||
    typeof limitationUsage.usedAmount !== 'string'
  ) {
    return null;
  }

  const tokenSymbol = policy.tokenTransferCondition.tokenSymbol;
  if (!tokenSymbol) {
    return null;
  }
  const windowResetAt =
    policy.limitationUsage.windowEnd instanceof Date
      ? policy.limitationUsage.windowEnd
      : new Date(policy.limitationUsage.windowEnd);

  return {
    policyLimit: policy.limitationUsage.amountThreshold,
    windowUsed: limitationUsage.usedAmount,
    currentTransactionAmount: currentTransactionAmount ?? '0',
    tokenSymbol,
    windowResetAt,
  };
}

/**
 * Compact policy indicator component for the transaction builder
 * Displays policy information in a 3-corner layout:
 * - Top-left: Policy name
 * - Top-right: Approval status
 */
export function TransactionBuilderPolicyIndicator({
  policy,
  limitationUsage,
  currentTransactionAmount,
  className,
}: TransactionBuilderPolicyIndicatorProps): React.ReactElement {
  const detailsText = formatPolicyDetailsText(policy);
  const tokenTransferLimit = getTokenTransferLimitProps({
    policy,
    limitationUsage,
    currentTransactionAmount,
  });

  return (
    <div
      className={cn(
        'mx-auto bg-zinc-100 dark:bg-zinc-800 border border-solid border-zinc-200 dark:border-zinc-700 rounded-[9px] px-[20px] py-[15px] flex flex-col gap-[5px] w-[80%] max-w-full',
        className
      )}
    >
      {/* Top Row: Policy name (left) and Approval status (right) */}
      <div className="flex items-center justify-between w-full">
        <p className="font-normal text-[12px] text-zinc-500 dark:text-zinc-400 text-left">
          Policy: {policy.name}
        </p>
        <p className="font-normal text-[12px] text-zinc-500 dark:text-zinc-400 text-right shrink-0">
          {getPolicyApprovalDisplay(policy)}
        </p>
      </div>

      {/* Bottom Row: Policy details */}
      <div className="flex gap-[5px] items-start flex-wrap">
        {detailsText.map((segment, index) => {
          if (segment.isPill) {
            return (
              <div
                key={index}
                className="bg-zinc-200 dark:bg-zinc-700 px-[6px] py-0 rounded-[8px] flex items-center justify-center"
              >
                <p className="font-medium text-[14px] text-zinc-800 dark:text-zinc-200 leading-normal break-all">
                  {segment.text}
                </p>
              </div>
            );
          } else {
            return (
              <p
                key={index}
                className="font-medium text-[14px] text-zinc-800 dark:text-zinc-200 leading-normal"
              >
                {segment.text}
              </p>
            );
          }
        })}
      </div>
      {tokenTransferLimit ? (
        <TokenTransferLimit {...tokenTransferLimit} />
      ) : null}
    </div>
  );
}
